// Portal — +HUB Subscriptions.
//
// Feb 2026 — Sam asked us to recover the original visual shell (from
// commit 372e8e0) and rewire it to the current +HUB backend. The
// visual structure below is byte-close to that recovered file; the
// state / networking layer is entirely new and talks to:
//
//   GET  /api/hub-plus/subscription              → status + catalogue + tiers
//   POST /api/hub-plus/subscription/preview-selection {selected_modules:[...]}
//                                                → server-priced quote
//   POST /api/hub-plus/subscription/confirm      → confirm the upgrade
//
// The Confirm modal fires with ``dry_run: true`` on purpose — the
// backend has all the entitlement / Xero repeating-invoice /
// GoCardless / cancellation logic wired up, but Sam is reviewing the
// recovered UI first and doesn't want any live Xero writes on the QA
// tester during that review. The old owner-facing hub_plus flow can
// be flipped to live later by just swapping ``dry_run`` to ``false``.
//
// UX rules already agreed with Sam and enforced in this pass:
//   * Bookings+ is removed while it's not yet available. Only three
//     selectable modules: My Territory+, Invoicing+, Marketing+.
//   * My Territory+ is mandatory as soon as Marketing+ or Invoicing+
//     is ticked (the two ride on top of Territory+'s CRM data).
//   * Bundle prices: £9 (any 1) / £16 (any 2) / £22 (any 3). We
//     deliberately do NOT surface a 4-module tier while Bookings+ is
//     out — the backend still knows about £27 but the UI hides it.
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Sparkles, MapPin, Megaphone, Receipt,
  CheckCircle2, Check, ArrowRight, Package, X, Loader2,
  AlertCircle, Clock,
} from "lucide-react";
import api from "@/lib/api";
import PortalPageHeading from "@/components/portal/PortalPageHeading";

// The three modules the franchisee can pick right now. Bookings+ is
// intentionally omitted from the frontend catalogue while it's not
// available; the backend still knows about it but hides it here.
// Order matches the canonical ``PAID_MODULE_KEYS`` order in
// ``hub_plus_pricing.py`` so the "Selected" chips line up with the
// admin/HQ readback.
const BOLT_ONS = [
  {
    key: "territory_plus",
    title: "My Territory+",
    icon: MapPin,
    accent: "#0ea5e9",
    recommended: true,
    blurb: "Claim customers as yours, plot them on the map, and run a light CRM on top.",
    features: [
      "\u201cClaim\u201d a client as yours and drop a marker on the map",
      "Add unlimited contacts of your own to your territory",
      "Basic CRM — notes, statuses, follow-up reminders",
      "Edit existing CQC database client info",
      "Group by care home group within your territory",
    ],
  },
  {
    key: "invoicing",
    title: "Invoicing+",
    icon: Receipt,
    accent: "#10b981",
    blurb: "Issue, send and reconcile your own customer invoices — linked to your contacts list.",
    features: [
      "Create, save and send invoices from inside the portal",
      "Linked to your customer contacts",
      "Branded invoice PDFs with your franchise details",
      "Add tax rates and discount rates",
      "Save / download as PDF in one click",
    ],
  },
  {
    key: "marketing",
    title: "Marketing+",
    icon: Megaphone,
    accent: "#f97316",
    blurb: "Send branded e-shots to your own customers from inside the portal.",
    features: [
      "Build basic e-shots with our drag-in templates",
      "Drop in images, headlines and your own copy",
      "Auto-co-branded with your franchise details",
      "Link straight to your Territory+ contacts",
    ],
  },
];

// Frontend fallback pricing — the source of truth is the server via
// the preview-selection endpoint; these values are used only for the
// static "Any 1 / Any 2 / Any 3" ladder chips and the pre-server
// echoed price the confirm modal re-checks against. If Paul ever
// changes prices in ``hub_plus_pricing.py`` the ladder will drift
// visually until this table is updated, but nothing bills wrong
// because the server-side quote wins.
const TIER_PRICES_INC_VAT = { 1: 9, 2: 16, 3: 22 };
const SINGLE_PRICE = TIER_PRICES_INC_VAT[1];

// Modules that require My Territory+ in the basket alongside them.
const NEEDS_TERRITORY = new Set(["invoicing", "marketing"]);

// -------------------------------------------------------------------
// Confirm-response banner classifier — Sep 2026 (Stage-4 cosmetic fix).
//
// The /hub-plus/subscription/confirm endpoint returns different shapes
// depending on what actually happened server-side. The banner MUST be
// driven by explicit response flags — never by the mere presence of
// ``first_billing_date_iso``. For a live UPDATE/REPLACE (module
// change) the backend intentionally omits ``first_billing_date_iso``
// because the existing Xero repeating-invoice schedule is preserved
// (Stage-4 DELETE→CREATE flow keeps StartDate/NextScheduledDate).
//
// Variants:
//   "live_update"  — live Xero DELETE→CREATE replacement (module
//                    change on an existing subscription). Signalled
//                    by ``xero_writes_performed === true`` AND either
//                    ``existing_xero_hub_repeating_invoice_id`` is
//                    present OR ``action`` contains "update".
//   "live_create"  — live first-time Xero create (brand-new
//                    subscription). ``xero_writes_performed === true``
//                    and no update signal. May include
//                    ``first_billing_date_iso``.
//   "preview_safe" — QA-record activation: DB entitlement flipped,
//                    no live Xero/GoCardless writes.
//   "dry_run"      — no live write, no DB flip (Phase-A review path,
//                    or any explicit dry_run response).
export function classifyConfirmResponse(info) {
  if (!info || typeof info !== "object") return "dry_run";
  const previewSafe = info.preview_safe_activated === true;
  const xeroWrote = info.xero_writes_performed === true;
  const action = typeof info.action === "string" ? info.action : "";
  if (previewSafe) return "preview_safe";
  if (xeroWrote) {
    const isUpdate =
      Boolean(info.existing_xero_hub_repeating_invoice_id) ||
      action.includes("update");
    return isUpdate ? "live_update" : "live_create";
  }
  return "dry_run";
}

// Post-confirm success banner. Keeps the emerald visual shell but
// routes the copy through ``classifyConfirmResponse`` so a live
// module-change replacement no longer falls through to the "Preview
// run" fallback just because ``first_billing_date_iso`` is missing.
export function ConfirmedBanner({ info }) {
  if (!info) return null;
  const variant = classifyConfirmResponse(info);
  const firstBillingDate = info.first_billing_date_iso;
  let heading;
  let body;
  switch (variant) {
    case "live_update":
      heading = "Your +HUB subscription has been updated successfully.";
      body = "Your updated module selection is now active and your billing has been updated.";
      break;
    case "live_create":
      heading = "Subscription active — modules unlocked.";
      body = firstBillingDate
        ? (<>First +HUB billing date: <strong>{firstBillingDate}</strong>.</>)
        : "Your +HUB add-ons are now active in the portal.";
      break;
    case "preview_safe":
      heading = "Subscription active — modules unlocked.";
      body = "Your +HUB add-ons are now available in the portal (Preview run — no live billing changes made).";
      break;
    case "dry_run":
    default:
      heading = "Selection confirmed with HQ.";
      body = "Preview run — no live billing changes made.";
      break;
  }
  return (
    <div
      className="rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-900 px-4 py-3 text-sm flex items-start gap-2"
      data-testid="subs-confirmed-block"
      data-variant={variant}
    >
      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
      <div>
        <div className="font-bold" data-testid="subs-confirmed-heading">
          {heading}
        </div>
        <div className="text-xs opacity-90 mt-0.5" data-testid="subs-confirmed-body">
          {body}
        </div>
      </div>
    </div>
  );
}


export default function PortalSubscriptionsPage() {
  // ------------------------------------------------------------------
  // Backend-fed state
  // ------------------------------------------------------------------
  //   status          — /hub-plus/subscription → status block
  //   entitled        — Set<moduleKey> — modules the franchisee has
  //                     access to right now (complimentary/active/
  //                     cancelled_pending). Rendered as "Active".
  //   loading, error  — GET state for the first page load.
  //   selected        — Set<moduleKey> — franchisee's live selection
  //                     (visual only, not persisted until confirm).
  //   serverQuote     — most recent /preview-selection response, gives
  //                     the authoritative price we display.
  // ------------------------------------------------------------------
  const [status, setStatus] = useState(null);
  const [entitled, setEntitled] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [serverQuote, setServerQuote] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [dependencyPrompt, setDependencyPrompt] = useState(null); // { addonKey, addonTitle }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmedInfo, setConfirmedInfo] = useState(null); // {activation_date_iso, ...}
  const [toast, setToast] = useState("");
  // Feb 2026 (Joanne Goodrum 403 fix). Sourced from
  // ``GET /hub-plus/subscription``. Only QA-flagged records ride the
  // preview-safe activation path; real franchisees hit the live path.
  const [isQaRecord, setIsQaRecord] = useState(false);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(msg);
    toastTimerRef.current = setTimeout(() => setToast(""), 6000);
  }, []);

  const loadSubscription = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/hub-plus/subscription");
      setStatus(data.status || null);
      // Gate the QA-only preview-safe activation flag on the confirm
      // request. Backend refuses ``preview_safe_activation=true`` on
      // any franchisee without ``is_qa_record=true``, so the flag
      // must never leave the client for a real franchisee — else
      // Joanne-style 403s ("preview_safe_activation is only permitted
      // against QA franchisee records…") surface in the UI.
      setIsQaRecord(Boolean(data.is_qa_record));
      // "Subscribed" is driven by the franchisee's ACTUAL saved
      // subscription (``current_selection.modules``), NOT by
      // ``status.entitled_modules``. During the complimentary window
      // ``entitled_modules`` returns all four paid modules (blanket
      // free access) — using that to drive the SUBSCRIBED pill would
      // mark every card as already-taken and hide the whole "pick
      // what you want to keep after complimentary ends" UX. See
      // hub_plus_entitlement.entitled_modules for the wider rule.
      const savedSubscription = (data.current_selection && data.current_selection.modules) || [];
      const savedClean = savedSubscription.filter(
        (k) => BOLT_ONS.some((b) => b.key === k),
      );
      setEntitled(new Set(savedClean));
      // Prime the visual selection with what they've already saved,
      // so any add-on flow naturally becomes "existing bundle + new
      // pick" and the server prices the correct tier.
      setSelected(new Set(savedClean));
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || "We couldn't load your +HUB subscription — please refresh in a moment.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  // ------------------------------------------------------------------
  // Re-price every time the selection changes.
  //
  // The server owns pricing (rejects any spoofed selection); we only
  // display what it returns. A tiny debounce keeps rapid clicks from
  // hammering the endpoint.
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      const list = Array.from(selected);
      if (list.length === 0) {
        setServerQuote(null);
        return;
      }
      setQuoteBusy(true);
      try {
        const { data } = await api.post("/hub-plus/subscription/preview-selection", {
          selected_modules: list,
        });
        if (!cancelled) setServerQuote(data.quote || null);
      } catch (err) {
        // Server refused (unknown module etc.) — surface but keep the
        // last known-good quote so the UI stays coherent.
        if (!cancelled) {
          console.warn("preview-selection failed:", err);
        }
      } finally {
        if (!cancelled) setQuoteBusy(false);
      }
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [selected]);

  // ------------------------------------------------------------------
  // Toggle logic — enforces the Territory+ dependency.
  // ------------------------------------------------------------------
  const toggle = (key) => {
    // Already active → can't unselect, can't reselect. Franchisee has
    // to use the cancel endpoint (a future turn) if they want to drop it.
    if (entitled.has(key)) return;

    // Currently ticked → untick. Cascade: dropping Territory+ drops
    // its dependents so the basket stays internally consistent.
    if (selected.has(key)) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(key);
        if (key === "territory_plus") {
          NEEDS_TERRITORY.forEach((k) => next.delete(k));
        }
        return next;
      });
      return;
    }

    // Adding a dependent module → require Territory+.
    if (NEEDS_TERRITORY.has(key) && !selected.has("territory_plus") && !entitled.has("territory_plus")) {
      const meta = BOLT_ONS.find((b) => b.key === key);
      setDependencyPrompt({ addonKey: key, addonTitle: meta?.title || key });
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const acceptDependencyPrompt = () => {
    if (!dependencyPrompt) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.add("territory_plus");
      next.add(dependencyPrompt.addonKey);
      return next;
    });
    setDependencyPrompt(null);
  };

  const selectedCount = selected.size;
  // Server-priced total (pence → £). Client fallback only if we
  // haven't heard back yet, which shows £0 rather than a stale value.
  const totalMonthly = serverQuote?.price_inc_vat_pence != null
    ? Math.round(serverQuote.price_inc_vat_pence / 100)
    : 0;
  const savingThisTier = Math.max(0, selectedCount * SINGLE_PRICE - totalMonthly);
  const hasNewSelection = useMemo(() => {
    // "New" = selected has at least one module the franchisee doesn't
    // already have entitlement for. Confirm button gates on this.
    for (const k of selected) {
      if (!entitled.has(k)) return true;
    }
    return false;
  }, [selected, entitled]);

  const submitConfirm = async () => {
    setConfirming(true); setConfirmError("");
    try {
      // QA-record franchisees stay on the preview-safe path — DB
      // entitlement flip, no Xero / GoCardless writes — so Sam can
      // rehearse the flow without touching billing. Real franchisees
      // MUST use the live path (``dry_run:false, allow_live_write:true``)
      // with no ``preview_safe_activation`` field, or the backend QA
      // guard refuses the request with a 403 (the bug Joanne hit).
      const payload = isQaRecord
        ? {
            selected_modules: Array.from(selected),
            client_price_inc_vat_pence: serverQuote?.price_inc_vat_pence ?? null,
            dry_run: true,
            preview_safe_activation: true,
          }
        : {
            selected_modules: Array.from(selected),
            client_price_inc_vat_pence: serverQuote?.price_inc_vat_pence ?? null,
            dry_run: false,
            allow_live_write: true,
          };
      const { data } = await api.post("/hub-plus/subscription/confirm", payload);
      setConfirmedInfo(data || {});
      setConfirmOpen(false);
      showToast(
        data?.preview_safe_activated
          ? "Subscription confirmed — your modules are now active."
          : "Subscription confirmed — your +HUB bolt-ons are now active.",
      );
      // Reload so entitled_modules → SUBSCRIBED pills refresh and
      // the sidebar picks up the newly-activated bolt-ons.
      loadSubscription();
    } catch (e) {
      setConfirmError(
        e?.response?.data?.detail
          || "Couldn't confirm right now — please try again in a moment or email HQ.",
      );
    } finally {
      setConfirming(false);
    }
  };


  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-stone-500" data-testid="portal-subscriptions-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your +HUB subscription…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-red-700" data-testid="portal-subscriptions-error">{error}</div>
    );
  }

  return (
    <div className="space-y-8 relative" data-testid="portal-subscriptions-page">
      <PortalPageHeading
        eyebrow="Account"
        icon={Sparkles}
        title="Subscriptions"
        subtitle="Add optional bolt-ons to your +HUB subscription — billed separately from your normal monthly franchise fees."
      />

      {/* Cancellation-in-notice banner — mirrors what the backend's
          effective_state returns so the franchisee always sees exactly
          where they stand. */}
      {status?.cancellation_notice_at && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-start gap-2"
          data-testid="subs-cancellation-banner"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Your +HUB subscription is in its 30-day notice period.</div>
            <div className="text-xs opacity-90 mt-0.5">
              Access remains active until the end of the notice window.
              {typeof status.cancellation_days_remaining === "number"
                && ` ${status.cancellation_days_remaining} day${status.cancellation_days_remaining === 1 ? "" : "s"} remaining.`}
            </div>
          </div>
        </div>
      )}

      {/* Complimentary-access countdown — uses the server-supplied
          ``complimentary_ends_at`` and ``days_remaining`` directly.
          Rendered only while the franchisee is IN the complimentary
          window (days_remaining is a non-negative integer). No
          frontend arithmetic — the backend already normalises the
          remaining-days figure and flags the 7-day warning. */}
      <ComplimentaryCountdownBanner status={status} />

      {/* Intro */}
      <div className="bg-white border border-stone-200 rounded-2xl px-6 py-6 sm:px-8 sm:py-7">
        <h2 className="font-display text-2xl sm:text-3xl font-black text-stone-950 tracking-tight">
          Supercharge your franchise with bolt-ons
        </h2>
        <p className="mt-3 text-stone-600 text-sm sm:text-base leading-relaxed lg:max-w-[80%]">
          Add any of the optional modules below to your +HUB subscription. Each bolt-on is just £{SINGLE_PRICE} a
          month — pick any two for £{TIER_PRICES_INC_VAT[2]} or all three for £{TIER_PRICES_INC_VAT[3]}. Your +HUB subscription is invoiced
          separately from your normal monthly franchise fees. Cancel any time with 30 days&apos; notice. All
          prices include VAT.
        </p>
        {/* Bundle ladder — three chips only while Bookings+ is unavailable. */}
        <div
          className="mt-6 p-4 sm:p-5 rounded-2xl border-2 border-[#dddd16] bg-[#dddd16]/10 shadow-sm"
          data-testid="subs-bundle-ladder-wrap"
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-stone-600">
                Bundle pricing
              </div>
              <div className="font-display text-xl sm:text-2xl font-black text-stone-950 leading-tight">
                The more you add, the more you save
              </div>
            </div>
            <div className="text-xs font-bold text-stone-700 px-2.5 py-1 bg-white border border-stone-300 rounded-full">
              All prices /month, inc VAT
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="subs-bundle-ladder">
            <TierBadge label="Any 1"           price={`£${TIER_PRICES_INC_VAT[1]}`} active={selectedCount === 1} />
            <TierBadge label="Any 2 — save £2" price={`£${TIER_PRICES_INC_VAT[2]}`} active={selectedCount === 2} highlight />
            <TierBadge label="All 3 — save £5" price={`£${TIER_PRICES_INC_VAT[3]}`} active={selectedCount === 3} highlight bestValue />
          </div>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="bolt-ons-grid">
        {BOLT_ONS.map((b) => {
          const active = entitled.has(b.key);
          const isSelected = selected.has(b.key);
          const Icon = b.icon;
          return (
            <div
              key={b.key}
              data-testid={`bolt-on-${b.key}`}
              className={`relative bg-white border-2 rounded-2xl flex flex-col transition-all overflow-hidden ${
                active
                  ? "border-emerald-500 shadow-lg"
                  : isSelected
                  ? "border-stone-950 shadow-lg"
                  : b.recommended
                  ? "border-stone-300 shadow-md"
                  : "border-stone-200"
              }`}
            >
              {b.recommended && !active && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#dedd0a] text-stone-950 text-[10px] font-black uppercase tracking-widest rounded-full z-10">
                  Most popular
                </div>
              )}
              {/* Full-width SUBSCRIBED banner — dominates the card so
                  the franchisee sees at a glance which modules are
                  already part of their paid subscription. Driven off
                  the saved ``current_selection.modules`` only — not
                  the complimentary entitlement grant. */}
              {active && (
                <div
                  className="w-full bg-emerald-500 text-white px-4 py-2.5 flex items-center justify-center gap-2 border-b-2 border-emerald-600"
                  data-testid={`bolt-on-active-${b.key}`}
                >
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2.6} />
                  <span className="font-display text-sm font-black uppercase tracking-[0.2em]">
                    Subscribed
                  </span>
                </div>
              )}

              {/* Card header — title + icon */}
              <div className="px-5 pt-7 pb-5 flex flex-col items-center text-center border-b border-stone-100">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                  style={{ backgroundColor: `${b.accent}1a`, color: b.accent }}
                >
                  <Icon className="w-7 h-7" strokeWidth={2.2} />
                </div>
                <h3 className="font-display text-xl font-black text-stone-950 tracking-tight">{b.title}</h3>
              </div>

              {/* Price */}
              <div className="px-5 py-3 border-b border-stone-100 text-center">
                <div className="font-display text-4xl sm:text-5xl font-black text-stone-950 leading-none tracking-tight">
                  £{SINGLE_PRICE}
                </div>
                <div className="text-[10px] text-stone-500 uppercase tracking-wider font-bold leading-tight mt-2">
                  Per month<br />inc VAT
                </div>
              </div>

              {/* Feature list */}
              <div className="px-5 py-5 flex-1">
                <ul className="space-y-2.5">
                  {b.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-stone-700">
                      <span
                        className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                        style={{ backgroundColor: `${b.accent}26`, color: b.accent }}
                      >
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span className="leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Checkbox + Upgrade button */}
              <div className="px-5 pb-5 pt-1 space-y-3">
                <label
                  className={`flex items-center gap-2.5 px-3 py-2.5 border rounded-lg cursor-pointer transition-colors text-sm font-medium ${
                    active
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900 cursor-not-allowed"
                      : isSelected
                      ? "bg-stone-950 text-white border-stone-950"
                      : "bg-stone-50 border-stone-200 text-stone-700 hover:bg-stone-100"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={active || isSelected}
                    disabled={active}
                    onChange={() => toggle(b.key)}
                    data-testid={`bolt-on-check-${b.key}`}
                    className="w-4 h-4 rounded accent-stone-950"
                  />
                  <span className="select-none">
                    {active ? "Included in your current +HUB subscription"
                      : isSelected ? "Selected"
                      : "Select"}
                  </span>
                </label>
                <button
                  type="button"
                  disabled={active || !isSelected}
                  onClick={() => setConfirmOpen(true)}
                  data-testid={`bolt-on-upgrade-${b.key}`}
                  className={`w-full px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition-colors ${
                    active
                      ? "bg-stone-100 text-stone-400 cursor-not-allowed"
                      : !isSelected
                      ? "bg-stone-100 text-stone-400 cursor-not-allowed"
                      : "bg-[#dedd0a] hover:brightness-95 text-stone-950"
                  }`}
                >
                  {active ? "Subscribed" : (
                    <>Upgrade now <ArrowRight className="w-3.5 h-3.5" /></>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dynamic pricing summary */}
      <div
        data-testid="bolt-on-bundle"
        className="relative rounded-2xl p-6 sm:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-6 bg-gradient-to-br from-stone-950 to-stone-800 text-white border-2 border-stone-800"
      >
        {selectedCount >= 2 && savingThisTier > 0 && (
          <div className="absolute -top-3 left-6 sm:left-8 px-3 py-1 bg-[#dedd0a] text-stone-950 text-[10px] font-black uppercase tracking-widest rounded-full inline-flex items-center gap-1">
            <Package className="w-3 h-3" /> Bundle applied · Save £{savingThisTier}/mo
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight">
            {selectedCount === 0 ? "Build your bundle" :
              selectedCount === 3 ? "All three bolt-ons unlocked" :
              `${selectedCount} bolt-on${selectedCount > 1 ? "s" : ""} selected`}
          </h3>
          <p className="text-stone-300 mt-1.5 text-sm sm:text-base leading-relaxed max-w-2xl">
            {selectedCount === 0
              ? "Tick any of the bolt-ons above. Pick any two for £16 a month, or all three for £22 — discounts apply automatically."
              : selectedCount === 1
              ? "Add one more bolt-on for £16 a month (any 2), or all three for £22 — discounts apply automatically."
              : selectedCount === 2
              ? "Nice — add the third bolt-on for just £6 more and we'll lock in the £22 all-three bundle."
              : "You've selected the full Creative Mojo toolkit at the best price we offer."}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {BOLT_ONS.map((b) => {
              const Icon = b.icon;
              const on = selected.has(b.key) || entitled.has(b.key);
              return (
                <span
                  key={b.key}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-[11px] font-bold uppercase tracking-wider ${
                    on
                      ? "bg-[#dedd0a] border-[#dedd0a] text-stone-950"
                      : "bg-white/10 border-white/15 text-white/70"
                  }`}
                >
                  <Icon className="w-3 h-3" /> {b.title}
                </span>
              );
            })}
          </div>
        </div>

        <div className="shrink-0 lg:text-right">
          <div className="flex items-baseline gap-2 lg:justify-end">
            {savingThisTier > 0 && (
              <span className="text-sm font-bold text-stone-400 line-through">£{selectedCount * SINGLE_PRICE}</span>
            )}
            <div className="flex items-start gap-0.5">
              <span className="text-xl font-black mt-1">£</span>
              <span className="font-display text-5xl sm:text-6xl font-black leading-none tracking-tight" data-testid="subs-bundle-total">
                {totalMonthly}
              </span>
              {quoteBusy && <Loader2 className="w-4 h-4 animate-spin ml-2 text-stone-400" />}
            </div>
          </div>
          <div className="text-[11px] text-stone-400 mt-1.5 uppercase tracking-wider font-bold lg:text-right">
            per month inc VAT
          </div>
        </div>
      </div>

      {/* Footer / summary bar */}
      <div className="bg-stone-100 border border-stone-200 rounded-2xl px-5 sm:px-7 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="subs-summary-bar">
        <div className="text-sm text-stone-600 leading-relaxed max-w-2xl">
          Your +HUB subscription will be invoiced separately from your normal monthly franchise fees.
          Cancel any bolt-on with 30 days&apos; notice.
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Selected total</div>
            <div className="font-display text-3xl font-black tracking-tight text-stone-950" data-testid="subs-total">
              £{totalMonthly} <span className="text-sm text-stone-500 font-bold">/ month inc VAT</span>
            </div>
          </div>
          <button
            type="button"
            disabled={!hasNewSelection || quoteBusy}
            onClick={() => setConfirmOpen(true)}
            data-testid="subs-confirm-upgrade"
            className="px-5 py-3 bg-[#dedd0a] hover:brightness-95 text-stone-950 font-bold text-xs uppercase tracking-wider rounded-lg flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Confirm upgrade <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* HQ-controlled cancellation notice — informational only. There
          is deliberately no franchisee-facing Cancel button; all
          cancellations go through Paul/Sandra so HQ can talk them
          through their options first, then trigger the 30-day notice
          via the HQ Subscriptions panel. */}
      <div
        className="rounded-2xl border-2 border-stone-200 bg-white px-5 sm:px-7 py-5 sm:py-6"
        data-testid="subs-cancel-info"
      >
        <h3 className="font-display text-lg sm:text-xl font-black text-stone-950 tracking-tight">
          Thinking of cancelling +HUB?
        </h3>
        <p className="text-sm text-stone-600 leading-relaxed mt-2 max-w-2xl">
          Just get in touch with HQ and we&apos;ll talk it through with you.
          If you still wish to cancel, we&apos;ll confirm your 30-day notice
          period and arrange everything for you.
        </p>
      </div>

      {/* Toast for success */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 max-w-sm bg-emerald-50 border border-emerald-300 text-emerald-900 px-4 py-3 rounded-xl shadow-lg flex items-start gap-2"
          data-testid="subs-toast"
        >
          <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">{toast}</div>
        </div>
      )}

      {/* Dependency prompt — Territory+ mandatory for Marketing+ / Invoicing+ */}
      {dependencyPrompt && (
        <div
          onClick={() => setDependencyPrompt(null)}
          className="fixed inset-0 z-[130] bg-stone-950/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="subs-dependency-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            <div className="px-5 py-3 flex items-center justify-between border-b border-stone-200 bg-[#0ea5e9] text-white">
              <div className="font-display text-xl font-black flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                My Territory+ required
              </div>
              <button
                onClick={() => setDependencyPrompt(null)}
                className="p-1.5 hover:bg-white/15 rounded-lg"
                data-testid="subs-dependency-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm text-stone-700 leading-relaxed">
              <p>
                <strong>{dependencyPrompt.addonTitle}</strong> sits on top of <strong>My Territory+</strong> —
                that&apos;s the bolt-on that holds your claimed customers, your map markers and your CRM data,
                which the other modules read from.
              </p>
              <p>
                You&apos;ll need <strong>My Territory+</strong> in your basket alongside it. Would you like to
                add both together? At this size, your bundle becomes <strong>£{TIER_PRICES_INC_VAT[2]} / month
                (Any 2)</strong> — a £2 saving versus buying them individually.
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-2 justify-end pt-2">
                <button
                  onClick={() => setDependencyPrompt(null)}
                  data-testid="subs-dependency-cancel"
                  className="px-4 py-2 text-sm font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={acceptDependencyPrompt}
                  data-testid="subs-dependency-accept"
                  className="px-4 py-2 text-sm font-bold uppercase tracking-wider bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg"
                >
                  Add both — £{TIER_PRICES_INC_VAT[2]} / month
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div
          onClick={() => !confirming && setConfirmOpen(false)}
          className="fixed inset-0 z-[120] bg-stone-950/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="subs-confirm-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="px-5 py-3 flex items-center justify-between border-b border-stone-200 bg-[#dedd0a]">
              <div className="font-display text-xl font-black text-stone-950">Confirm your upgrade</div>
              <button onClick={() => !confirming && setConfirmOpen(false)} className="p-1.5 hover:bg-stone-950/10 rounded-lg" data-testid="subs-confirm-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-stone-700 leading-relaxed">
                You&apos;re adding the following bolt-on{selectedCount === 1 ? "" : "s"} to your Creative Mojo subscription:
              </p>
              <ul className="space-y-2" data-testid="subs-confirm-list">
                {Array.from(selected).map((k) => {
                  const b = BOLT_ONS.find((x) => x.key === k);
                  if (!b) return null;
                  const Icon = b.icon;
                  return (
                    <li key={k} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-stone-50 border border-stone-200">
                      <span
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${b.accent}1a`, color: b.accent }}
                      >
                        <Icon className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-stone-900 text-sm">{b.title}</div>
                        <div className="text-xs text-stone-500">{b.blurb}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="bg-stone-100 border border-stone-200 rounded-xl px-4 py-3 text-sm leading-relaxed text-stone-800">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <span className="font-bold">Total added to your monthly bill</span>
                  <span className="font-display text-2xl font-black text-stone-950" data-testid="subs-confirm-total">
                    £{totalMonthly}
                    <span className="text-xs text-stone-500 font-bold ml-1">/ month inc VAT</span>
                  </span>
                </div>
                <p className="text-xs text-stone-600">
                  Your +HUB subscription will be <strong>invoiced separately from your normal monthly
                  franchise fees</strong>. Cancel any bolt-on with 30 days&apos; notice.
                </p>
              </div>

              {confirmError && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{confirmError}</span>
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-stone-50 border-t border-stone-200 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={confirming}
                data-testid="subs-confirm-cancel"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 hover:bg-white text-stone-700 rounded-lg disabled:opacity-50"
              >
                Not yet
              </button>
              <button
                onClick={submitConfirm}
                disabled={confirming || quoteBusy}
                data-testid="subs-confirm-submit"
                className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 hover:bg-stone-800 text-[#dedd0a] rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {confirming ? "Confirming…" : "Confirm my selection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-confirm callout — routes through ConfirmedBanner so
          the copy is driven by explicit response flags
          (xero_writes_performed / preview_safe_activated / action /
          existing_xero_hub_repeating_invoice_id), not by the mere
          presence of ``first_billing_date_iso`` (which the backend
          intentionally omits for module-change replacements). See
          classifyConfirmResponse above. */}
      <ConfirmedBanner info={confirmedInfo} />
    </div>
  );
}

// -------------------------------------------------------------------
// ComplimentaryCountdownBanner
//
// Rendered only while the franchisee is IN the complimentary window.
// All the numbers come straight off the ``status`` block returned by
// ``GET /hub-plus/subscription`` — the backend already computes
// ``days_remaining`` and flips ``show_seven_day_warning`` when needed
// (see ``_status_block`` in ``hub_plus_subscription_routes.py``).
//
// Visual language: solid Creative Mojo yellow/lime background with a
// prominent right-hand roundel showing the day count. The roundel
// escalates through three tiers as the window shrinks:
//
//   * days > 7  →  Creative Mojo black+lime  (default, not urgent)
//   * 3 < days ≤ 7 → orange  (heads-up warning)
//   * days ≤ 3     → red     (immediate urgency)
//
// The tier is derived from ``status.days_remaining`` directly so we
// don't depend on any single-flag server flag — that stays available
// for other places in the UI, but this banner needs finer control.
// -------------------------------------------------------------------
function ComplimentaryCountdownBanner({ status }) {
  if (!status) return null;
  const days = status.days_remaining;
  // Only render when the franchisee is inside the complimentary
  // window: `days_remaining` is a non-negative integer (0 = ends
  // today) AND their effective state is complimentary. This means
  // paid-active / cancelled_pending / expired all skip the banner.
  const inCompWindow =
    typeof days === "number"
    && days >= 0
    && status.effective_state === "complimentary_active";
  if (!inCompWindow) return null;

  const endsAt = status.complimentary_ends_at;
  const endsFmt = _formatEndsDate(endsAt);

  // Three-tier urgency escalation driven off the server-supplied
  // day count. Kept as an explicit switch so it's obvious at a
  // glance which tier each branch produces.
  let roundelTier = "normal";
  if (days <= 3) roundelTier = "red";
  else if (days <= 7) roundelTier = "orange";
  const roundelStyle =
    roundelTier === "red"    ? { backgroundColor: "#dc2626", borderColor: "#991b1b", color: "#ffffff" } :
    roundelTier === "orange" ? { backgroundColor: "#f97316", borderColor: "#c2410c", color: "#ffffff" } :
                               { backgroundColor: "#0c0a09", borderColor: "#0c0a09", color: "#dedd0a" };

  return (
    <div
      data-testid="comp-countdown-banner"
      data-urgency={roundelTier}
      className="relative rounded-2xl overflow-hidden border-2 border-[#c8c800] shadow-md flex flex-col sm:flex-row items-stretch"
      style={{ backgroundColor: "#dedd0a" }}
    >
      <div className="flex-1 min-w-0 px-5 py-5 sm:px-7 sm:py-6 flex flex-col justify-center gap-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-black text-stone-900/70">
          <Clock className="w-3.5 h-3.5" />
          Complimentary access
        </div>
        <h2
          className="font-display text-2xl sm:text-3xl font-black text-stone-950 tracking-tight leading-tight"
          data-testid="comp-countdown-heading"
        >
          Your complimentary +HUB access ends{endsFmt ? " on " : ""}
          {endsFmt && (
            <span className="whitespace-nowrap" data-testid="comp-countdown-end-date">
              {endsFmt}
            </span>
          )}
          {!endsFmt && " soon"}
        </h2>
        <p className="text-sm sm:text-base text-stone-900/85 leading-relaxed max-w-2xl">
          Choose which +HUB modules you&apos;d like to keep after this date. Your
          standard Creative Mojo Portal access continues as normal.
        </p>
      </div>
      <div
        data-testid="comp-countdown-roundel-wrap"
        className="shrink-0 flex items-center justify-center px-5 pb-6 sm:pb-0 sm:pr-8 sm:pl-2"
      >
        <div
          data-testid="comp-countdown-roundel"
          className="w-28 h-28 sm:w-32 sm:h-32 rounded-full flex flex-col items-center justify-center border-4 shadow-lg"
          style={roundelStyle}
        >
          <div
            className="font-display text-4xl sm:text-5xl font-black leading-none"
            data-testid="comp-countdown-days"
          >
            {days}
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest mt-1">
            Day{days === 1 ? "" : "s"} left
          </div>
        </div>
      </div>
    </div>
  );
}

// Format ISO date/timestamp → "Fri 27 Feb 2026" (UK locale).
// Falls back to the raw string if parsing fails.
function _formatEndsDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}


function TierBadge({ label, price, active, highlight, bestValue }) {
  return (
    <div
      data-testid={`tier-badge-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`relative px-3 py-4 rounded-xl border-2 text-center transition-all ${
        active
          ? "bg-stone-950 text-white border-stone-950 shadow-lg scale-[1.04]"
          : bestValue
          ? "bg-white border-stone-900 shadow-md"
          : highlight
          ? "bg-white border-[#dddd16] shadow-sm"
          : "bg-white border-stone-300"
      }`}
    >
      {bestValue && (
        <span
          className={`absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] rounded-full shadow-sm ${
            active ? "bg-[#dddd16] text-stone-950" : "bg-stone-950 text-[#dddd16]"
          }`}
          data-testid="tier-badge-best-value"
        >
          Best value
        </span>
      )}
      <div className={`font-display text-2xl sm:text-3xl font-black leading-none ${active ? "text-white" : "text-stone-950"}`}>
        {price}
      </div>
      <div
        className={`text-[10px] sm:text-[11px] uppercase tracking-wider font-bold mt-2 ${
          active ? "text-stone-200" : "text-stone-700"
        }`}
      >
        {label}
      </div>
    </div>
  );
}
